/**
 * La portée d'une saisie de notes : client, site, compte, sujet, report sur les graphiques.
 *
 * Trois écrans posaient la même question et la résolvaient chacun à sa façon : la modale
 * (`NotesModal.jsx`), le calendrier, et maintenant l'import. La cascade client → sites →
 * comptes y était écrite deux fois, avec déjà des écarts — le choix d'office du site unique
 * existait dans la modale et manquait au calendrier.
 *
 * Ce hook tient l'état et les règles ; `PorteeDeNote.jsx` ne fait que les afficher.
 */

import { useState, useEffect, useMemo, useRef } from 'react';

/**
 * Valeur de portée venue du header, ramenée à la convention des listes.
 *
 * Le header dit `null` pour « aucun client » et `'all'` pour « tous les sites » ; les
 * `<select>` emploient la chaîne vide. Sans cette conversion, `value="all"` ne correspond à
 * aucune `<option>` et la liste retombe sur la première, muette.
 */
export const valeurDePortee = (valeur) => (!valeur || valeur === 'all' ? '' : valeur);

/**
 * @param {object} options
 * @param {Array} options.sites tous les sites connus
 * @param {Array} options.accounts tous les comptes connus
 * @param {string|null} options.selectedClientId client courant du header
 * @param {string|null} options.selectedSiteId site courant du header
 * @param {boolean} [options.surGraphiqueParDefaut] valeur initiale de la case
 * @param {Function} [options.auChangement] appelé quand le header impose une nouvelle portée
 */
export function usePorteeDeNote({
  sites = [],
  accounts = [],
  selectedClientId,
  selectedSiteId,
  surGraphiqueParDefaut = false,
  auChangement
}) {
  const contexte = (typeof window !== 'undefined' && window.currentNotesContext) || {};

  const [clientId, setClientId] = useState(() => contexte.clientId || valeurDePortee(selectedClientId));
  const [siteId, setSiteId] = useState(() => contexte.siteId || valeurDePortee(selectedSiteId));
  const [accountId, setAccountId] = useState(() => contexte.accountId || '');
  const [sujet, setSujet] = useState('');
  const [surGraphique, setSurGraphique] = useState(surGraphiqueParDefaut);

  const sitesDuClient = useMemo(() => (
    clientId ? sites.filter(s => s.clientId === clientId) : sites
  ), [sites, clientId]);

  /**
   * Les comptes que la portée courante laisse voir.
   *
   * Un site désigné borne au site ; à défaut, un client borne à ses sites. Sans l'un ni
   * l'autre, tous les comptes — c'est la seule liste qui ait un sens quand on n'a rien
   * restreint.
   */
  const comptesDuPerimetre = useMemo(() => {
    if (siteId) return accounts.filter(a => a.siteId === siteId);
    if (clientId) {
      const idsDesSites = sitesDuClient.map(s => s.id);
      return accounts.filter(a => idsDesSites.includes(a.siteId));
    }
    return accounts;
  }, [accounts, sitesDuClient, clientId, siteId]);

  /** La plateforme n'est pas saisie : elle se déduit du compte, comme dans la modale. */
  const compteChoisi = comptesDuPerimetre.find(a => a.sourceId === accountId) || null;

  /**
   * Suivre le client et le site choisis dans le header.
   *
   * Le suivi se déclenche sur le **changement** de la valeur du header, pas à chaque rendu :
   * une portée corrigée ici doit tenir tant que le header ne bouge pas. Sans cela, les listes
   * ne seraient que décoratives.
   */
  const porteeDuHeader = useRef({
    client: valeurDePortee(selectedClientId),
    site: valeurDePortee(selectedSiteId)
  });
  useEffect(() => {
    const client = valeurDePortee(selectedClientId);
    const site = valeurDePortee(selectedSiteId);
    const precedent = porteeDuHeader.current;
    if (precedent.client === client && precedent.site === site) return;

    porteeDuHeader.current = { client, site };
    setClientId(client);
    setSiteId(site);
    if (auChangement) auChangement();
    // `auChangement` est recréé à chaque rendu par les appelants ; le mettre en dépendance
    // rejouerait l'effet sans que la portée ait bougé.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, selectedSiteId]);

  /**
   * Un client qui n'a qu'un site : on le choisit d'office.
   *
   * Laisser « Tous les sites » alors qu'il n'y en a qu'un ne propose aucun choix réel, et
   * enregistre les notes sans site — donc introuvables au filtrage par site.
   */
  useEffect(() => {
    if (!clientId || siteId) return;
    if (sitesDuClient.length === 1) setSiteId(sitesDuClient[0].id);
  }, [clientId, siteId, sitesDuClient]);

  /**
   * Un compte qui sort du périmètre est abandonné, sans quoi la liste afficherait une valeur
   * absente de ses options et les notes partiraient sur un compte qu'on ne voit plus.
   */
  useEffect(() => {
    if (!accountId) return;
    if (!comptesDuPerimetre.some(a => a.sourceId === accountId)) setAccountId('');
  }, [accountId, comptesDuPerimetre]);

  return {
    clientId,
    siteId,
    accountId,
    sujet,
    surGraphique,
    setClientId,
    setSiteId,
    setAccountId,
    setSujet,
    setSurGraphique,
    sitesDuClient,
    comptesDuPerimetre,
    compteChoisi,
    /** Ce qu'une note créée sous cette portée doit porter. */
    corpsDePortee: {
      clientId: clientId || null,
      siteId: siteId || null,
      accountId: accountId || null,
      platform: compteChoisi?.type || null
    }
  };
}
